import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppUser } from '../boot/session';
import { BLOCK_CATEGORIES } from '../cad/blocks/standardLibrary';
import { STANDARD_LAYERS } from '../cad/model/document';
import {
  deleteBlock,
  listBlocks,
  listEditRequests,
  proposeEdit,
  reviewEdit,
  type BlockEditChanges,
  type BlockEditRequestRow,
  type BlockMasterRow,
} from '../data/blockRepo';
import { useUserDirectory } from '../hooks/useUserDirectory';
import { Modal, ModalCancel } from '../shell/Modal';
import { OneUI } from '../shell/oneUi';
import { ErrorState, LoadingState } from '../shell/State';

/**
 * 블록 관리 화면 (작업지시서 P4-06)
 *
 * ── 이 화면이 실제로 하는 일 ────────────────────────────────────────────
 *
 *   단가·규격·이격을 **확인하고, 승인을 거쳐 고친다**. 이 표의 값이 견적과
 *   통로 폭 검증에 그대로 쓰이므로, 한 사람이 단독으로 바꿀 수 없게 한다.
 *
 * ── 승인 흐름 ────────────────────────────────────────────────────────────
 *
 *   관리자가 "수정 제안"을 내면 `cad_block` 은 그대로다. 다른 관리자가
 *   승인해야만 실제로 반영된다(`cad_apply_block_edit()`, 0009 마이그레이션).
 *   본인이 낸 제안은 본인이 승인/반려할 수 없다 — 화면과 DB 양쪽에서 막는다.
 *
 * ⚠️ 단가는 자리표시자로 시작한 값일 수 있다. 견적에 그대로 쓰기 전에
 *    구매부서 확인을 거치세요.
 */

export function BlockAdminPage({ user }: { user: AppUser }) {
  const { nameOf } = useUserDirectory();
  const [category, setCategory] = useState<string>('all');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<'code' | 'cost' | 'shelf'>('code');

  const [blocks, setBlocks] = useState<BlockMasterRow[]>([]);
  const [requests, setRequests] = useState<BlockEditRequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [editing, setEditing] = useState<BlockMasterRow | null>(null);
  const [rejecting, setRejecting] = useState<BlockEditRequestRow | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [busyRequestId, setBusyRequestId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const canManage = user.role === 'admin' || user.role === 'cad_admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [b, r] = await Promise.all([listBlocks(), listEditRequests('pending')]);
      setBlocks(b);
      setRequests(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = blocks.filter((b) => {
      if (category !== 'all' && b.categoryCode !== category) return false;
      if (!q) return true;
      return b.name.toLowerCase().includes(q) || b.blockCode.toLowerCase().includes(q);
    });

    return [...list].sort((a, b) => {
      if (sort === 'cost') return unitCost(b) - unitCost(a);
      if (sort === 'shelf') return (b.shelfLengthPerUnitM ?? 0) - (a.shelfLengthPerUnitM ?? 0);
      return a.blockCode.localeCompare(b.blockCode);
    });
  }, [blocks, category, query, sort]);

  const totalCost = rows.reduce((s, b) => s + unitCost(b), 0);
  const missingCost = rows.filter((b) => unitCost(b) === 0).length;
  const pendingByBlock = useMemo(() => {
    const m = new Map<string, BlockEditRequestRow>();
    for (const r of requests) if (r.status === 'pending') m.set(r.blockId, r);
    return m;
  }, [requests]);

  async function submitProposal(block: BlockMasterRow, changes: BlockEditChanges, note: string) {
    setBusy('제안 등록 중…');
    try {
      await proposeEdit(block.id, block.blockCode, changes, note || undefined);
      setEditing(null);
      setNotice(`${block.blockCode} 수정 제안을 등록했습니다. 다른 관리자의 승인이 필요합니다.`);
      await load();
    } catch (e) {
      setNotice(`제안 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  async function approve(req: BlockEditRequestRow) {
    setBusy('승인 처리 중…');
    setBusyRequestId(req.id);
    try {
      await reviewEdit(req.id, 'approve');
      setNotice(`${req.blockCode} 수정을 승인했습니다.`);
      await load();
    } catch (e) {
      setNotice(`승인 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
      setBusyRequestId(null);
    }
  }

  async function reject(req: BlockEditRequestRow, reason: string) {
    setBusy('반려 처리 중…');
    setBusyRequestId(req.id);
    try {
      await reviewEdit(req.id, 'reject', reason);
      setRejecting(null);
      setNotice(`${req.blockCode} 수정을 반려했습니다.`);
      await load();
    } catch (e) {
      setNotice(`반려 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
      setBusyRequestId(null);
    }
  }

  async function removeBlock(block: BlockMasterRow) {
    if (
      !(await OneUI.confirm(
        '이미 이 블록을 쓴 도면은 영향받지 않습니다 (도면 안에 도형이 그대로 남아 있습니다). ' +
          '다만 앞으로 새로 넣을 수는 없습니다.',
        { title: `"${block.name}" (${block.blockCode}) 블록을 삭제할까요?`, okText: '삭제', danger: true }
      ))
    ) {
      return;
    }
    setBusy('삭제 중…');
    try {
      await deleteBlock(block.id, block.blockCode);
      setNotice(`${block.name} 을(를) 삭제했습니다.`);
      await load();
    } catch (e) {
      setNotice(`삭제 실패: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h1 className="page__title">블록 라이브러리 관리</h1>
          <p className="page__desc">
            표준 블록 {blocks.length}종. 견적은 이 표의 단가로 계산됩니다. 수정은 다른 관리자의
            승인이 필요합니다.
          </p>
        </div>
        <span className="badge">P4-06</span>
      </header>

      <p className="banner banner--warn" role="note">
        <strong>단가는 참고값일 수 있습니다.</strong> 실제 견적 전에 구매부서 단가로 확인하세요.
        이격 거리는 통로 폭·피난 검토에 그대로 쓰이므로 임의로 낮추지 마세요.
      </p>

      {notice && (
        <p className="banner" role="status" style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
          <span>{notice}</span>
          <button type="button" className="btn btn--sm" onClick={() => setNotice(null)} aria-label="알림 닫기">
            ✕
          </button>
        </p>
      )}

      {error && <ErrorState title="블록 목록을 불러올 수 없습니다" message={error} onRetry={load} />}

      {requests.filter((r) => r.status === 'pending').length > 0 && (
        <div className="card">
          <h2 className="card__title">대기 중인 수정 제안 ({requests.length}건)</h2>
          <div className="table-wrap">
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>블록</th>
                  <th>제안자</th>
                  <th>일시</th>
                  <th>변경 내용</th>
                  <th>메모</th>
                  <th aria-label="처리"></th>
                </tr>
              </thead>
              <tbody>
                {requests
                  .filter((r) => r.status === 'pending')
                  .map((r) => {
                    const isSelf = r.proposedBy === user.email;
                    return (
                      <tr key={r.id}>
                        <td className="mono">{r.blockCode}</td>
                        <td title={r.proposedBy}>{nameOf(r.proposedBy)}</td>
                        <td className="mono">{new Date(r.proposedAt).toLocaleString('ko-KR')}</td>
                        <td>{summarizeChanges(r.changes)}</td>
                        <td className="muted">{r.note ?? '—'}</td>
                        <td>
                          {canManage ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                type="button"
                                className="btn btn--primary btn--sm"
                                disabled={isSelf || busy !== null}
                                title={isSelf ? '본인이 제안한 수정은 승인할 수 없습니다' : undefined}
                                onClick={() => void approve(r)}
                              >
                                {busyRequestId === r.id ? '승인 중…' : '승인'}
                              </button>
                              <button
                                type="button"
                                className="btn btn--sm"
                                disabled={isSelf || busy !== null}
                                title={isSelf ? '본인이 제안한 수정은 반려할 수 없습니다' : undefined}
                                onClick={() => setRejecting(r)}
                              >
                                반려
                              </button>
                            </div>
                          ) : (
                            <span className="muted">검토 대기</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div className="toolbar">
          <input
            className="input input--search"
            type="search"
            placeholder="블록 검색 (이름 · 코드)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="select"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            aria-label="분류"
          >
            <option value="all">전체 분류</option>
            {BLOCK_CATEGORIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.icon} {c.name}
              </option>
            ))}
          </select>
          <select
            className="select"
            value={sort}
            onChange={(e) => setSort(e.target.value as typeof sort)}
            aria-label="정렬"
          >
            <option value="code">코드순</option>
            <option value="cost">단가 높은순</option>
            <option value="shelf">진열 연장순</option>
          </select>
        </div>

        {loading ? (
          <LoadingState label="블록 목록 불러오는 중…" />
        ) : (
          <>
            <p className="muted" style={{ margin: '0 0 10px' }}>
              {rows.length}종 · 단가 합계 {totalCost.toLocaleString('ko-KR')}원
              {missingCost > 0 && ` · 단가 미입력 ${missingCost}종`}
            </p>

            <div className="table-wrap">
              <table className="table table--compact">
                <thead>
                  <tr>
                    <th>코드</th>
                    <th>이름</th>
                    <th>분류</th>
                    <th>레이어</th>
                    <th className="num">규격(mm)</th>
                    <th className="num">진열(m)</th>
                    <th className="num">면적(㎡)</th>
                    <th>이격(mm)</th>
                    <th>설비</th>
                    <th className="num">단가(원)</th>
                    <th aria-label="수정"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((b) => {
                    const pending = pendingByBlock.get(b.id);
                    return (
                      <tr key={b.id}>
                        <td className="mono">{b.blockCode}</td>
                        <td>{b.name}</td>
                        <td>{categoryName(b.categoryCode)}</td>
                        <td className="mono">{b.defaultLayer}</td>
                        <td className="num">{sizeOf(b)}</td>
                        <td className="num">
                          {b.shelfLengthPerUnitM ? b.shelfLengthPerUnitM.toFixed(1) : '—'}
                        </td>
                        <td className="num">{areaOf(b) > 0 ? areaOf(b).toFixed(2) : '—'}</td>
                        <td>{clearanceOf(b)}</td>
                        <td>{utilityOf(b)}</td>
                        <td className="num">
                          {unitCost(b) > 0 ? (
                            unitCost(b).toLocaleString('ko-KR')
                          ) : (
                            <span className="muted">미입력</span>
                          )}
                        </td>
                        <td>
                          {canManage && (
                            <div style={{ display: 'flex', gap: 6 }}>
                              {pending ? (
                                <span
                                  className="tag"
                                  title={`${nameOf(pending.proposedBy)} 제안, 승인 대기중`}
                                >
                                  제안 대기중
                                </span>
                              ) : (
                                <button type="button" className="btn btn--sm" onClick={() => setEditing(b)}>
                                  수정
                                </button>
                              )}
                              {b.staticEntities && (
                                <button
                                  type="button"
                                  className="btn btn--sm btn--danger"
                                  disabled={busy !== null}
                                  title="도면에서 등록한 블록만 여기서 지울 수 있습니다"
                                  onClick={() => void removeBlock(b)}
                                >
                                  삭제
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td colSpan={11} className="muted">
                        조건에 맞는 블록이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {!canManage && (
        <div className="card">
          <h2 className="card__title">단가·규격 수정</h2>
          <p className="muted">블록 마스터를 수정하려면 CAD 관리자 권한이 필요합니다.</p>
        </div>
      )}

      {editing && (
        <EditProposalModal
          block={editing}
          busy={busy !== null}
          onClose={() => setEditing(null)}
          onSubmit={(changes, note) => void submitProposal(editing, changes, note)}
        />
      )}

      {rejecting && (
        <RejectReasonModal
          request={rejecting}
          busy={busy !== null}
          onClose={() => setRejecting(null)}
          onSubmit={(reason) => void reject(rejecting, reason)}
        />
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * 수정 제안 모달
 * -------------------------------------------------------------------------- */

function EditProposalModal({
  block,
  busy,
  onClose,
  onSubmit,
}: {
  block: BlockMasterRow;
  busy: boolean;
  onClose: () => void;
  onSubmit: (changes: BlockEditChanges, note: string) => void;
}) {
  const [name, setName] = useState(block.name);
  const [categoryCode, setCategoryCode] = useState(block.categoryCode);
  const [defaultLayer, setDefaultLayer] = useState(block.defaultLayer);
  const [widthMm, setWidthMm] = useState(numToStr(block.widthMm));
  const [depthMm, setDepthMm] = useState(numToStr(block.depthMm));
  const [heightMm, setHeightMm] = useState(numToStr(block.heightMm));
  const [shelfM, setShelfM] = useState(numToStr(block.shelfLengthPerUnitM));
  const [frontMm, setFrontMm] = useState(numToStr(block.clearance.frontMm));
  const [sideMm, setSideMm] = useState(numToStr(block.clearance.sideMm));
  const [rearMm, setRearMm] = useState(numToStr(block.clearance.rearMm));
  const [power, setPower] = useState(numToStr(block.utility.power ?? null));
  const [water, setWater] = useState(!!block.utility.water);
  const [drain, setDrain] = useState(!!block.utility.drain);
  const [heatLoadW, setHeatLoadW] = useState(numToStr(block.utility.heatLoadW));
  const [standardKrw, setStandardKrw] = useState(numToStr(block.cost.standardKrw));
  const [installKrw, setInstallKrw] = useState(numToStr(block.cost.installKrw));
  const [note, setNote] = useState('');

  const previewWidth = strToNum(widthMm);
  const previewDepth = strToNum(depthMm);
  const previewArea =
    previewWidth && previewDepth ? ((previewWidth * previewDepth) / 1_000_000).toFixed(2) : '—';

  function submit() {
    const changes: BlockEditChanges = {};
    if (name !== block.name) changes.name = name;
    if (categoryCode !== block.categoryCode) changes.category_code = categoryCode;
    if (defaultLayer !== block.defaultLayer) changes.default_layer = defaultLayer;
    if (strToNum(widthMm) !== block.widthMm) changes.width_mm = strToNum(widthMm) ?? undefined;
    if (strToNum(depthMm) !== block.depthMm) changes.depth_mm = strToNum(depthMm) ?? undefined;
    if (strToNum(heightMm) !== block.heightMm) changes.height_mm = strToNum(heightMm) ?? undefined;
    if (strToNum(shelfM) !== block.shelfLengthPerUnitM) {
      changes.shelf_length_per_unit_m = strToNum(shelfM) ?? undefined;
    }
    const clearance = {
      frontMm: strToNum(frontMm) ?? undefined,
      sideMm: strToNum(sideMm) ?? undefined,
      rearMm: strToNum(rearMm) ?? undefined,
    };
    if (JSON.stringify(clearance) !== JSON.stringify(block.clearance)) changes.clearance = clearance;
    const utility = {
      power: strToNum(power),
      water,
      drain,
      heatLoadW: strToNum(heatLoadW) ?? undefined,
    };
    if (JSON.stringify(utility) !== JSON.stringify(block.utility)) changes.utility = utility;
    const cost = {
      standardKrw: strToNum(standardKrw) ?? undefined,
      installKrw: strToNum(installKrw) ?? undefined,
    };
    if (JSON.stringify(cost) !== JSON.stringify(block.cost)) changes.cost = cost;

    onSubmit(changes, note);
  }

  return (
    <Modal
      title={`수정 제안 — ${block.blockCode}`}
      desc="다른 관리자가 승인해야 실제로 반영됩니다. 코드는 식별자라 바꿀 수 없습니다."
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button type="button" className="btn btn--primary" onClick={submit} disabled={busy}>
            {busy ? '등록 중…' : '제안 등록'}
          </button>
        </>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <label className="field">
          <span className="field__label">이름</span>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">분류</span>
          <select className="select" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)}>
            {BLOCK_CATEGORIES.map((c) => (
              <option key={c.code} value={c.code}>
                {c.icon} {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">기본 레이어</span>
          <select className="select" value={defaultLayer} onChange={(e) => setDefaultLayer(e.target.value)}>
            {STANDARD_LAYERS.map((l) => (
              <option key={l.code} value={l.code}>
                {l.code} — {l.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field__label">진열 연장(m)</span>
          <input className="input" inputMode="decimal" value={shelfM} onChange={(e) => setShelfM(e.target.value)} />
        </label>

        <label className="field">
          <span className="field__label">폭(mm)</span>
          <input className="input" inputMode="numeric" value={widthMm} onChange={(e) => setWidthMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">깊이(mm)</span>
          <input className="input" inputMode="numeric" value={depthMm} onChange={(e) => setDepthMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">높이(mm)</span>
          <input className="input" inputMode="numeric" value={heightMm} onChange={(e) => setHeightMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">면적(㎡) — 자동계산</span>
          <input className="input" value={previewArea} disabled />
        </label>

        <label className="field">
          <span className="field__label">이격 · 전면(mm)</span>
          <input className="input" inputMode="numeric" value={frontMm} onChange={(e) => setFrontMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">이격 · 측면(mm)</span>
          <input className="input" inputMode="numeric" value={sideMm} onChange={(e) => setSideMm(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">이격 · 후면(mm)</span>
          <input className="input" inputMode="numeric" value={rearMm} onChange={(e) => setRearMm(e.target.value)} />
        </label>
        <div />

        <label className="field">
          <span className="field__label">전원(V)</span>
          <input className="input" inputMode="numeric" value={power} onChange={(e) => setPower(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">발열(W)</span>
          <input className="input" inputMode="numeric" value={heatLoadW} onChange={(e) => setHeatLoadW(e.target.value)} />
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={water} onChange={(e) => setWater(e.target.checked)} />
          <span className="field__label">급수 필요</span>
        </label>
        <label className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" checked={drain} onChange={(e) => setDrain(e.target.checked)} />
          <span className="field__label">배수 필요</span>
        </label>

        <label className="field">
          <span className="field__label">단가 · 표준(원)</span>
          <input
            className="input"
            inputMode="numeric"
            value={standardKrw}
            onChange={(e) => setStandardKrw(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field__label">단가 · 설치(원)</span>
          <input
            className="input"
            inputMode="numeric"
            value={installKrw}
            onChange={(e) => setInstallKrw(e.target.value)}
          />
        </label>
      </div>

      <label className="field" style={{ marginTop: 4 }}>
        <span className="field__label">제안 메모 (선택)</span>
        <textarea
          className="input"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="왜 바꾸는지 남겨두면 승인자가 판단하기 쉽습니다."
        />
      </label>
    </Modal>
  );
}

function RejectReasonModal({
  request,
  busy,
  onClose,
  onSubmit,
}: {
  request: BlockEditRequestRow;
  busy: boolean;
  onClose: () => void;
  onSubmit: (reason: string) => void;
}) {
  const [reason, setReason] = useState('');

  return (
    <Modal
      title={`수정 반려 — ${request.blockCode}`}
      desc="반려 사유는 제안자가 볼 수 있도록 반드시 남겨야 합니다."
      onClose={onClose}
      footer={
        <>
          <ModalCancel disabled={busy}>취소</ModalCancel>
          <button
            type="button"
            className="btn btn--danger"
            disabled={busy || !reason.trim()}
            onClick={() => onSubmit(reason)}
          >
            {busy ? '반려 처리 중…' : '반려'}
          </button>
        </>
      }
    >
      <label className="field">
        <span className="field__label">반려 사유</span>
        <textarea
          className="input"
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="예: 단가 근거 자료 없이 임의로 낮춘 값입니다."
        />
      </label>
    </Modal>
  );
}

/* -------------------------------------------------------------------------- */

function numToStr(v: number | null | undefined): string {
  return v === null || v === undefined ? '' : String(v);
}

function strToNum(s: string): number | null {
  const t = s.trim();
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function summarizeChanges(changes: BlockEditChanges): string {
  const bits: string[] = [];
  if (changes.name) bits.push(`이름→${changes.name}`);
  if (changes.category_code) bits.push(`분류→${changes.category_code}`);
  if (changes.default_layer) bits.push(`레이어→${changes.default_layer}`);
  if (changes.width_mm !== undefined || changes.depth_mm !== undefined) {
    bits.push(`규격→${changes.width_mm ?? '—'}×${changes.depth_mm ?? '—'}`);
  }
  if (changes.shelf_length_per_unit_m !== undefined) bits.push(`진열→${changes.shelf_length_per_unit_m}m`);
  if (changes.clearance) bits.push('이격 변경');
  if (changes.utility) bits.push('설비 변경');
  if (changes.cost) {
    const c = changes.cost;
    bits.push(`단가→${((c.standardKrw ?? 0) + (c.installKrw ?? 0)).toLocaleString('ko-KR')}원`);
  }
  return bits.length > 0 ? bits.join(' · ') : '변경 없음';
}

function categoryName(code: string): string {
  return BLOCK_CATEGORIES.find((c) => c.code === code)?.name ?? code;
}

function unitCost(b: BlockMasterRow): number {
  return (b.cost.standardKrw ?? 0) + (b.cost.installKrw ?? 0);
}

function areaOf(b: BlockMasterRow): number {
  if (!b.widthMm || !b.depthMm) return 0;
  return (b.widthMm * b.depthMm) / 1_000_000;
}

function sizeOf(b: BlockMasterRow): string {
  if (b.widthMm && b.depthMm) return `${b.widthMm}×${b.depthMm}`;
  return '고정';
}

function clearanceOf(b: BlockMasterRow): string {
  const c = b.clearance;
  const bits: string[] = [];
  if (c.frontMm) bits.push(`전 ${c.frontMm}`);
  if (c.sideMm) bits.push(`측 ${c.sideMm}`);
  if (c.rearMm) bits.push(`후 ${c.rearMm}`);
  return bits.length > 0 ? bits.join(' / ') : '—';
}

function utilityOf(b: BlockMasterRow): string {
  const u = b.utility;
  const bits: string[] = [];
  if (u.power) bits.push(`${u.power}V`);
  if (u.water) bits.push('급수');
  if (u.drain) bits.push('배수');
  if (u.heatLoadW) bits.push(`${u.heatLoadW}W`);
  return bits.length > 0 ? bits.join(' · ') : '—';
}
